import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { CatalogOffering, CatalogService, MarketplaceStore, StorefrontPublication } from '../../catalog/catalog.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { MarketplaceStoreSelectorComponent } from '../../../shared/design-system/marketplace-store-selector.component';
import { parseSearchOpsStorefrontBinding, serializeSearchOpsStorefrontBinding } from './growth-search-ops.models';

interface PublicationOption {
  id: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-storefront-binding',
  imports: [FormsModule, SelectModule, AinFormFieldComponent, MarketplaceStoreSelectorComponent],
  template: `
    <div class="binding-grid">
      <ain-form-field
        [label]="t('growth.searchops.fields.storefrontKey')"
        [hint]="t('growth.searchops.fields.storefrontKey.hint')">
        <ain-marketplace-store-selector
          [(ngModel)]="storeId"
          [autoSelectDefault]="false"
          [showClear]="true"
          placeholderKey="growth.searchops.fields.storefrontKey.placeholder"
          (ngModelChange)="onStoreChange($event)" />
      </ain-form-field>
      <ain-form-field
        [label]="t('growth.searchops.fields.storefrontPublicationId')"
        [hint]="t('growth.searchops.fields.storefrontPublicationId.hint')">
        <p-select
          [options]="publicationOptions()"
          optionLabel="label"
          optionValue="id"
          [(ngModel)]="publicationId"
          [showClear]="true"
          [filter]="true"
          filterBy="label"
          [placeholder]="t('growth.searchops.fields.storefrontPublicationId.placeholder')"
          [disabled]="!storeId"
          appendTo="body"
          (ngModelChange)="emitBinding()" />
      </ain-form-field>
    </div>
  `,
  styles: [`
    .binding-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    @media (max-width: 720px) { .binding-grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthSearchOpsStorefrontBindingComponent implements OnInit {
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);
  private incomingJson = '{}';

  readonly stores = signal<MarketplaceStore[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly publicationOptions = signal<PublicationOption[]>([]);

  storeId: string | null = null;
  publicationId: string | null = null;

  @Input()
  set payloadJson(value: string | null | undefined) {
    const next = value || '{}';
    if (next === this.incomingJson)
      return;
    this.incomingJson = next;
    this.applyFromJson(next);
  }

  @Output() readonly payloadJsonChange = new EventEmitter<string>();

  ngOnInit(): void {
    this.catalog.listMarketplaceStores().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: stores => {
        this.stores.set(stores);
        this.applyFromJson(this.incomingJson);
      },
      error: () => this.stores.set([])
    });
    this.catalog.listOfferings().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: offerings => this.offerings.set(offerings),
      error: () => this.offerings.set([])
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  onStoreChange(storeId: string | null): void {
    this.storeId = storeId;
    this.publicationId = null;
    this.loadPublications(storeId, null);
    this.emitBinding();
  }

  emitBinding(): void {
    const store = this.stores().find(item => item.id === this.storeId);
    const json = serializeSearchOpsStorefrontBinding({
      storefrontKey: store?.storeKey ?? '',
      storefrontPublicationId: this.publicationId ?? ''
    });
    this.incomingJson = json;
    this.payloadJsonChange.emit(json);
  }

  private applyFromJson(json: string): void {
    const binding = parseSearchOpsStorefrontBinding(json);
    const store = this.stores().find(item =>
      item.storeKey.localeCompare(binding.storefrontKey, undefined, { sensitivity: 'accent' }) === 0);
    this.storeId = store?.id ?? null;
    this.publicationId = binding.storefrontPublicationId || null;
    this.loadPublications(this.storeId, this.publicationId);
  }

  private loadPublications(storeId: string | null, keepPublicationId: string | null): void {
    if (!storeId) {
      this.publicationOptions.set([]);
      this.publicationId = null;
      return;
    }

    this.catalog.listStorefrontPublications(storeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: rows => {
        this.publicationOptions.set(rows.map(row => this.toPublicationOption(row)));
        if (keepPublicationId && rows.some(row => row.id === keepPublicationId))
          this.publicationId = keepPublicationId;
        else if (this.publicationId && !rows.some(row => row.id === this.publicationId))
          this.publicationId = null;
      },
      error: () => this.publicationOptions.set([])
    });
  }

  private toPublicationOption(row: StorefrontPublication): PublicationOption {
    const offering = this.offerings().find(item => item.id === row.offeringId);
    const title = offering?.title?.trim() || offering?.code?.trim() || this.t('growth.searchops.fields.storefrontPublicationFallback');
    return { id: row.id, label: `${title} — ${row.status}` };
  }
}
